/**
 * API Client for Voice Assessment Platform
 *
 * Features:
 * - JWT-based authentication (matches harness pattern)
 * - Request/response tracking for UI display
 * - Automatic token refresh
 * - WebCrypto hashing for artifact uploads
 */

import { mintJwt, isTokenExpired } from './jwt';
import type {
  ApiResult,
  ApiRequest,
  Assessment,
  AssessmentListResponse,
  ArtifactWithVersions,
  ArtifactLink,
  ArtifactLinkListResponse,
  InitArtifactResponse,
  Rubric,
  RubricListResponse,
  GenerateScoreResponse,
  ScoreListResponse,
  TranscriptionListResponse,
  WebhookSubscription,
  SubscriptionListResponse,
  DeliveryListResponse,
  ReplayDeliveryResponse,
  ExportBundle,
  CreateSubmissionResponse,
  CommitSubmissionResponse,
  CreateLearnerTokenResponse,
  TriggerGenerationResponse,
  GenerationStatusResponse,
  AssessmentPackageResponse,
  UpdateCompetencyWeightsRequest,
  PublishAssessmentRequest,
  SubmitQuizResponse,
  CompetenciesResponse,
  UpdateCompetenciesRequest,
  RefinePackageRequest,
  RefinePackageResponse,
} from './types';

// API prefix - confirmed from src/api/router.ts line 408
const API_PREFIX = '/api/v1/internal';

export interface ApiClientConfig {
  baseUrl: string;
  tenantId: string;
  actorId: string;
  jwtSecret: string;
}

export class ApiClient {
  private config: ApiClientConfig;
  private token: string | null = null;
  private useFixedToken = false; // When true, don't auto-refresh token
  private tokenProvider: (() => Promise<string | null>) | null = null;

  constructor(config: ApiClientConfig) {
    this.config = config;
  }

  setTokenProvider(provider: (() => Promise<string | null>) | null): void {
    this.tokenProvider = provider;
    this.token = null;
    this.useFixedToken = false;
  }

  /**
   * Sets a fixed token that won't be auto-refreshed.
   * Used for learner access where token comes from URL.
   */
  setFixedToken(token: string): void {
    this.token = token;
    this.useFixedToken = true;
  }

  /**
   * Updates the client configuration.
   */
  updateConfig(config: Partial<ApiClientConfig>): void {
    this.config = { ...this.config, ...config };
    // Invalidate token when config changes
    this.token = null;
  }

  /**
   * Gets a valid JWT token, refreshing if needed.
   * Priority: fixed token > token provider (Supabase) > legacy mintJwt.
   */
  private async getToken(): Promise<string> {
    // Fixed tokens (from URL) should not be auto-refreshed
    if (this.useFixedToken && this.token) {
      return this.token;
    }

    // Token provider (Supabase auth) — second priority
    if (this.tokenProvider) {
      const token = await this.tokenProvider();
      if (token) {
        this.token = token;
        return token;
      }
      throw new Error('Authentication required. Please sign in.');
    }

    // Legacy fallback: only when no provider is set
    if (!this.token || isTokenExpired(this.token)) {
      this.token = await mintJwt({
        tenantId: this.config.tenantId,
        secret: this.config.jwtSecret,
        actorId: this.config.actorId,
      });
    }
    return this.token;
  }

  /**
   * Builds headers for API requests.
   * Returns both the actual headers and a display version with redacted JWT.
   */
  private async buildHeaders(): Promise<{ headers: Record<string, string>; displayHeaders: Record<string, string> }> {
    const token = await this.getToken();
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    };
    const displayHeaders: Record<string, string> = {
      'Content-Type': 'application/json',
      'Authorization': 'Bearer [REDACTED]',
    };
    return { headers, displayHeaders };
  }

  /**
   * Makes an API request and returns both request and response for display.
   */
  private async request<T>(
    method: string,
    path: string,
    body?: unknown
  ): Promise<ApiResult<T>> {
    const url = `${this.config.baseUrl}${API_PREFIX}${path}`;
    const { headers, displayHeaders } = await this.buildHeaders();

    const request: ApiRequest = {
      method,
      url,
      headers: displayHeaders,
      body,
    };

    try {
      const response = await fetch(url, {
        method,
        headers,
        body: body ? JSON.stringify(body) : undefined,
      });

      const contentType = response.headers.get('content-type');
      let data: T | undefined;
      let error: { error: string; message: string; details?: Record<string, string>; statusCode: number } | undefined;

      if (contentType?.includes('application/json')) {
        const json = await response.json();
        if (response.ok) {
          data = json as T;
        } else {
          error = json;
        }
      }

      return {
        request,
        response: {
          status: response.status,
          statusText: response.statusText,
          data,
          error,
        },
      };
    } catch (err) {
      return {
        request,
        response: {
          status: 0,
          statusText: 'Network Error',
          error: {
            error: 'network_error',
            message: err instanceof Error ? err.message : 'Unknown error',
            statusCode: 0,
          },
        },
      };
    }
  }

  // ==================== Assessments ====================

  async createAssessment(data: {
    name: string;
    objective: string;
    assessmentType: 'pre' | 'post';
    assessmentMode?: 'voice' | 'quiz';
    metadata?: Record<string, unknown>;
    externalReferenceId?: string;
    sourceSystem?: string;
    rubricVersion?: string;
    traceId?: string;
  }): Promise<ApiResult<Assessment>> {
    return this.request<Assessment>('POST', '/assessments', data);
  }

  async updateAssessment(
    id: string,
    data: {
      name?: string;
      objective?: string;
      assessmentType?: 'pre' | 'post';
      version: number;
    }
  ): Promise<ApiResult<Assessment>> {
    return this.request<Assessment>('PATCH', `/assessments/${id}`, data);
  }

  async getAssessment(id: string): Promise<ApiResult<Assessment>> {
    return this.request<Assessment>('GET', `/assessments/${id}`);
  }

  async listAssessments(params?: {
    limit?: number;
    cursor?: string;
    status?: string;
  }): Promise<ApiResult<AssessmentListResponse>> {
    const query = new URLSearchParams();
    if (params?.limit) query.set('limit', params.limit.toString());
    if (params?.cursor) query.set('cursor', params.cursor);
    if (params?.status) query.set('status', params.status);
    const qs = query.toString();
    return this.request<AssessmentListResponse>('GET', `/assessments${qs ? `?${qs}` : ''}`);
  }

  /**
   * Creates a scoped learner token for an assessment.
   * The token is only valid for accessing the specified assessment.
   */
  async createLearnerToken(assessmentId: string): Promise<ApiResult<CreateLearnerTokenResponse>> {
    return this.request<CreateLearnerTokenResponse>('POST', `/assessments/${assessmentId}/learner-token`, {});
  }

  /**
   * Creates a short-lived preview token (30 min) for trainer to preview learner experience.
   */
  async createPreviewToken(assessmentId: string): Promise<ApiResult<CreateLearnerTokenResponse>> {
    return this.request<CreateLearnerTokenResponse>('POST', `/assessments/${assessmentId}/preview-token`, {});
  }

  // ==================== Generation ====================

  /**
   * Triggers AI generation of assessment package.
   * Assessment must be in draft status.
   */
  async triggerGeneration(
    assessmentId: string,
    data?: { additionalContext?: string }
  ): Promise<ApiResult<TriggerGenerationResponse>> {
    return this.request<TriggerGenerationResponse>(
      'POST',
      `/assessments/${assessmentId}/generate`,
      data ?? {}
    );
  }

  /**
   * Gets the current generation status for an assessment.
   * Returns null status if no generation has been attempted.
   */
  async getGenerationStatus(assessmentId: string): Promise<ApiResult<GenerationStatusResponse>> {
    return this.request<GenerationStatusResponse>(
      'GET',
      `/assessments/${assessmentId}/generation-status`
    );
  }

  /**
   * Gets the full AI-generated assessment package.
   * Returns 404 if no package exists.
   */
  async getAssessmentPackage(assessmentId: string): Promise<ApiResult<AssessmentPackageResponse>> {
    return this.request<AssessmentPackageResponse>('GET', `/assessments/${assessmentId}/package`);
  }

  /**
   * Refines an assessment package based on a trainer's instruction.
   * Assessment must be in draft status with an existing package.
   */
  async refinePackage(
    assessmentId: string,
    data: RefinePackageRequest
  ): Promise<ApiResult<RefinePackageResponse>> {
    return this.request<RefinePackageResponse>(
      'POST',
      `/assessments/${assessmentId}/package/refine`,
      data
    );
  }

  /**
   * Suggests competencies for a training program using AI.
   */
  async suggestCompetencies(data: {
    trainingName: string;
    objective?: string;
  }): Promise<ApiResult<{ suggestions: string[] }>> {
    return this.request<{ suggestions: string[] }>('POST', '/suggest-competencies', data);
  }

  /**
   * Updates competency weights for an assessment package.
   * Assessment must be in draft status. Uses optimistic locking.
   */
  async updateCompetencyWeights(
    assessmentId: string,
    data: UpdateCompetencyWeightsRequest
  ): Promise<ApiResult<AssessmentPackageResponse>> {
    return this.request<AssessmentPackageResponse>(
      'PATCH',
      `/assessments/${assessmentId}/package/weights`,
      data
    );
  }

  /**
   * Publishes an assessment (transitions from draft to pending).
   * Assessment must have an AI-generated package. Uses optimistic locking.
   */
  async publishAssessment(
    assessmentId: string,
    data: PublishAssessmentRequest
  ): Promise<ApiResult<Assessment>> {
    return this.request<Assessment>('POST', `/assessments/${assessmentId}/publish`, data);
  }

  /**
   * Clones a published assessment to create a new draft version.
   * Cannot clone draft or invalidated assessments.
   */
  async cloneAssessment(
    assessmentId: string,
    options?: { clonePackage?: boolean }
  ): Promise<ApiResult<Assessment>> {
    return this.request<Assessment>('POST', `/assessments/${assessmentId}/clone`, options ?? {});
  }

  // ==================== Two-Step Generation (F-QUIZ-002) ====================

  /**
   * Triggers generation of competencies only (Step 1).
   * Assessment must be in draft status.
   */
  async triggerCompetencyGeneration(
    assessmentId: string,
    data?: { additionalContext?: string }
  ): Promise<ApiResult<TriggerGenerationResponse>> {
    return this.request<TriggerGenerationResponse>(
      'POST',
      `/assessments/${assessmentId}/package/generate-competencies`,
      data ?? {}
    );
  }

  /**
   * Triggers generation of content (scenario, persona, rubric) using existing competencies (Step 2).
   * Requires a completed competencies-only job.
   */
  async triggerContentGeneration(
    assessmentId: string
  ): Promise<ApiResult<TriggerGenerationResponse>> {
    return this.request<TriggerGenerationResponse>(
      'POST',
      `/assessments/${assessmentId}/package/generate-content`,
      {}
    );
  }

  /**
   * Gets competencies from a completed competencies-only job.
   * Used for trainer review before content generation.
   */
  async getCompetencies(assessmentId: string): Promise<ApiResult<CompetenciesResponse>> {
    return this.request<CompetenciesResponse>(
      'GET',
      `/assessments/${assessmentId}/package/competencies`
    );
  }

  /**
   * Updates competencies before content generation.
   * Uses optimistic locking via version field.
   */
  async updateCompetencies(
    assessmentId: string,
    data: UpdateCompetenciesRequest
  ): Promise<ApiResult<CompetenciesResponse>> {
    return this.request<CompetenciesResponse>(
      'PATCH',
      `/assessments/${assessmentId}/package/competencies`,
      data
    );
  }

  // ==================== Quiz (F-QUIZ-005) ====================

  /**
   * Submits quiz answers for scoring.
   * Assessment must be in quiz mode with generated questions.
   */
  async submitQuiz(
    assessmentId: string,
    answers: { questionId: string; selectedAnswerIndex: number }[]
  ): Promise<ApiResult<SubmitQuizResponse>> {
    return this.request<SubmitQuizResponse>(
      'POST',
      `/assessments/${assessmentId}/quiz/submit`,
      { answers }
    );
  }

  // ==================== Artifacts ====================

  async initArtifact(data: {
    mimeType: string;
    filename?: string;
    metadata?: Record<string, unknown>;
  }): Promise<ApiResult<InitArtifactResponse>> {
    return this.request<InitArtifactResponse>('POST', '/artifacts/init', data);
  }

  async commitArtifact(
    id: string,
    data: {
      contentHash: string;
      contentSize: number;
      versionId: string;
      storagePath: string;
    }
  ): Promise<ApiResult<ArtifactWithVersions>> {
    return this.request<ArtifactWithVersions>('POST', `/artifacts/${id}/commit`, data);
  }

  async getArtifact(id: string): Promise<ApiResult<ArtifactWithVersions>> {
    return this.request<ArtifactWithVersions>('GET', `/artifacts/${id}`);
  }

  // Note: listArtifacts removed - backend does not support GET /artifacts endpoint

  /**
   * Uploads file or blob to presigned URL.
   * This is a direct upload to S3, not through our API.
   * Accepts both File (for file uploads) and Blob (for audio recordings).
   */
  async uploadToPresignedUrl(
    url: string,
    data: Blob | File,
    mimeType: string
  ): Promise<{ success: boolean; error?: string }> {
    try {
      const response = await fetch(url, {
        method: 'PUT',
        headers: {
          'Content-Type': mimeType,
          'x-amz-server-side-encryption': 'AES256',
        },
        body: data,
      });

      if (!response.ok) {
        const text = await response.text();
        return { success: false, error: `Upload failed: ${response.status} ${text}` };
      }

      return { success: true };
    } catch (err) {
      return {
        success: false,
        error: err instanceof Error ? err.message : 'Upload failed',
      };
    }
  }

  // ==================== Artifact Links ====================

  async listArtifactLinks(params?: {
    limit?: number;
    cursor?: string;
    artifactId?: string;
    assessmentId?: string;
  }): Promise<ApiResult<ArtifactLinkListResponse>> {
    const query = new URLSearchParams();
    if (params?.limit) query.set('limit', params.limit.toString());
    if (params?.cursor) query.set('cursor', params.cursor);
    if (params?.artifactId) query.set('artifactId', params.artifactId);
    if (params?.assessmentId) query.set('assessmentId', params.assessmentId);
    const qs = query.toString();
    return this.request<ArtifactLinkListResponse>('GET', `/artifact-links${qs ? `?${qs}` : ''}`);
  }

  /**
   * Creates a link between an artifact version and an assessment.
   */
  async createArtifactLink(data: {
    artifactId: string;
    artifactVersionId: string;
    assessmentId: string;
    linkType: 'source' | 'derived' | 'reference';
  }): Promise<ApiResult<ArtifactLink>> {
    return this.request<ArtifactLink>('POST', '/artifact-links', data);
  }

  // ==================== Rubrics ====================

  async listRubrics(params?: {
    limit?: number;
    cursor?: string;
    status?: 'draft' | 'published' | 'deprecated';
  }): Promise<ApiResult<RubricListResponse>> {
    const query = new URLSearchParams();
    if (params?.limit) query.set('limit', params.limit.toString());
    if (params?.cursor) query.set('cursor', params.cursor);
    if (params?.status) query.set('status', params.status);
    const qs = query.toString();
    return this.request<RubricListResponse>('GET', `/rubrics${qs ? `?${qs}` : ''}`);
  }

  async getRubric(id: string): Promise<ApiResult<Rubric>> {
    return this.request<Rubric>('GET', `/rubrics/${id}`);
  }

  async createRubric(data: {
    name: string;
    description?: string;
    passingScore: number;
    dimensions: Array<{
      name: string;
      description?: string;
      weight: number;
      rules: Array<{
        type: 'keyword' | 'regex';
        pattern: string;
        score: number;
        required?: boolean;
        caseSensitive?: boolean;
      }>;
    }>;
  }): Promise<ApiResult<Rubric>> {
    return this.request<Rubric>('POST', '/rubrics', data);
  }

  async updateRubric(
    id: string,
    data: {
      name?: string;
      description?: string;
      passingScore?: number;
      dimensions?: Array<{
        id?: string;
        name: string;
        description?: string;
        weight: number;
        rules: Array<{
          id?: string;
          type: 'keyword' | 'regex';
          pattern: string;
          score: number;
          required?: boolean;
          caseSensitive?: boolean;
        }>;
      }>;
    }
  ): Promise<ApiResult<Rubric>> {
    return this.request<Rubric>('PATCH', `/rubrics/${id}`, data);
  }

  async publishRubric(id: string): Promise<ApiResult<Rubric>> {
    return this.request<Rubric>('POST', `/rubrics/${id}/publish`, {});
  }

  async deprecateRubric(id: string): Promise<ApiResult<Rubric>> {
    return this.request<Rubric>('POST', `/rubrics/${id}/deprecate`, {});
  }

  // ==================== Transcription ====================

  /**
   * Gets transcription jobs for a specific audio artifact version.
   * Returns all transcription jobs that used this audio as their source.
   */
  async getTranscriptsByAudio(
    audioArtifactVersionId: string
  ): Promise<ApiResult<TranscriptionListResponse>> {
    return this.request<TranscriptionListResponse>(
      'GET',
      `/transcripts/by-audio/${audioArtifactVersionId}`
    );
  }

  // ==================== Scoring ====================

  async generateScore(data: {
    transcriptArtifactVersionId: string;
    rubricId?: string;
    rubricVersion?: number;
  }): Promise<ApiResult<GenerateScoreResponse>> {
    return this.request<GenerateScoreResponse>('POST', '/scores/generate', data);
  }

  async getScoresByTranscript(
    transcriptArtifactVersionId: string
  ): Promise<ApiResult<ScoreListResponse>> {
    return this.request<ScoreListResponse>(
      'GET',
      `/scores/by-transcript/${transcriptArtifactVersionId}`
    );
  }

  // ==================== Subscriptions ====================

  async createSubscription(data: {
    endpointUrl: string;
    subscribedEventTypes: string[];
  }): Promise<ApiResult<WebhookSubscription>> {
    return this.request<WebhookSubscription>('POST', '/subscriptions', data);
  }

  async listSubscriptions(params?: {
    limit?: number;
    cursor?: string;
    status?: string;
  }): Promise<ApiResult<SubscriptionListResponse>> {
    const query = new URLSearchParams();
    if (params?.limit) query.set('limit', params.limit.toString());
    if (params?.cursor) query.set('cursor', params.cursor);
    if (params?.status) query.set('status', params.status);
    const qs = query.toString();
    return this.request<SubscriptionListResponse>('GET', `/subscriptions${qs ? `?${qs}` : ''}`);
  }

  async updateSubscriptionStatus(
    id: string,
    status: 'active' | 'paused'
  ): Promise<ApiResult<WebhookSubscription>> {
    return this.request<WebhookSubscription>('PATCH', `/subscriptions/${id}/status`, { status });
  }

  // ==================== Deliveries ====================

  async listDeliveries(params: {
    subscriptionId: string;
    limit?: number;
    cursor?: string;
  }): Promise<ApiResult<DeliveryListResponse>> {
    const query = new URLSearchParams();
    query.set('subscriptionId', params.subscriptionId);
    if (params.limit) query.set('limit', params.limit.toString());
    if (params.cursor) query.set('cursor', params.cursor);
    return this.request<DeliveryListResponse>('GET', `/deliveries?${query.toString()}`);
  }

  async replayDelivery(data: {
    outboxEventId: string;
    subscriptionId: string;
  }): Promise<ApiResult<ReplayDeliveryResponse>> {
    return this.request<ReplayDeliveryResponse>('POST', '/deliveries/replay', data);
  }

  // ==================== Exports ====================

  async exportAssessmentBundle(
    assessmentId: string,
    options?: {
      includeArtifacts?: boolean;
      includeContent?: boolean;
      includeTranscripts?: boolean;
      includeScores?: boolean;
      includeAuditEvents?: boolean;
    }
  ): Promise<ApiResult<ExportBundle>> {
    return this.request<ExportBundle>('POST', `/exports/assessment/${assessmentId}`, options ?? {});
  }

  // ==================== Submissions (Learner Convenience API) ====================

  /**
   * Creates a submission with presigned upload URL.
   * Note: Submission is a thin alias for Assessment - submissionId IS the assessmentId.
   */
  async createSubmission(data: {
    mimeType: string;
    externalReferenceId?: string;
    metadata?: Record<string, unknown>;
    rubricId?: string;
    filename?: string;
  }): Promise<ApiResult<CreateSubmissionResponse>> {
    return this.request<CreateSubmissionResponse>('POST', '/submissions', data);
  }

  /**
   * Commits a submission after uploading audio to presigned URL.
   */
  async commitSubmission(
    submissionId: string,
    data: {
      artifactId: string;
      contentHash: string;
      contentSize: number;
      pendingVersionId: string;
      storagePath: string;
      rubricId?: string;
    }
  ): Promise<ApiResult<CommitSubmissionResponse>> {
    return this.request<CommitSubmissionResponse>('POST', `/submissions/${submissionId}/commit`, data);
  }


  // ==================== Artifact Content ====================

  /**
   * Fetches artifact content as text.
   */
  async getArtifactContentAsText(id: string): Promise<ApiResult<string>> {
    const url = `${this.config.baseUrl}${API_PREFIX}/artifacts/${id}/content`;
    const { headers, displayHeaders } = await this.buildHeaders();

    const request: ApiRequest = {
      method: 'GET',
      url,
      headers: displayHeaders,
    };

    try {
      const response = await fetch(url, {
        method: 'GET',
        headers,
      });

      if (response.ok) {
        const text = await response.text();
        return {
          request,
          response: {
            status: response.status,
            statusText: response.statusText,
            data: text,
          },
        };
      } else {
        const json = await response.json();
        return {
          request,
          response: {
            status: response.status,
            statusText: response.statusText,
            error: json,
          },
        };
      }
    } catch (err) {
      return {
        request,
        response: {
          status: 0,
          statusText: 'Network Error',
          error: {
            error: 'network_error',
            message: err instanceof Error ? err.message : 'Unknown error',
            statusCode: 0,
          },
        },
      };
    }
  }
}

// Create a singleton instance with default config
// Use empty baseUrl to leverage Vite proxy (avoids CORS issues)
const defaultConfig: ApiClientConfig = {
  baseUrl: import.meta.env.VITE_API_BASE_URL ?? '',
  tenantId: import.meta.env.VITE_TENANT_ID ?? '',
  actorId: import.meta.env.VITE_ACTOR_ID ?? 'operator-ui',
  jwtSecret: import.meta.env.VITE_JWT_SECRET ?? 'test-secret-for-development',
};

export const apiClient = new ApiClient(defaultConfig);

/**
 * Creates an ApiClient that uses a pre-supplied JWT token.
 * Used by learner UI where token comes from URL query param.
 * Token is NOT stored - only used for this client instance.
 * Token will NOT be auto-refreshed (learner must get new link if expired).
 */
export function createApiClientWithToken(token: string): ApiClient {
  const client = new ApiClient({
    baseUrl: import.meta.env.VITE_API_BASE_URL ?? '',
    tenantId: '', // Not used when token is pre-supplied
    actorId: '',  // Not used when token is pre-supplied
    jwtSecret: '', // Not used when token is pre-supplied
  });
  // Set fixed token - won't be auto-refreshed
  client.setFixedToken(token);
  return client;
}
